import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, FolderOpen, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { GRANULARITIES, DATE_PRESETS } from './constants';

export const SavedViewsControls = ({
  savedViews, showSaveInput, setShowSaveInput,
  showSavedViews, setShowSavedViews,
  saveViewName, setSaveViewName,
  granularity, datePreset, selectedMetrics,
  onSaveView, onLoadView, onDeleteView
}) => {
  return (
    <div className="flex items-center gap-2 relative">
      <Button variant="outline" size="sm" onClick={() => setShowSaveInput(!showSaveInput)} data-testid="save-view-btn">
        <Save className="h-4 w-4 mr-1" />
        Save View
      </Button>
      <Button variant="outline" size="sm" onClick={() => setShowSavedViews(!showSavedViews)} data-testid="load-view-btn">
        <FolderOpen className="h-4 w-4 mr-1" />
        Views {savedViews.length > 0 && `(${savedViews.length})`}
      </Button>

      <AnimatePresence>
        {showSaveInput && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-4 w-72 z-40"
          >
            <p className="text-sm font-medium text-slate-700 mb-2">Save Current View</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={saveViewName}
                onChange={e => setSaveViewName(e.target.value)}
                placeholder="e.g., Monthly Premium Trend"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/30"
                onKeyDown={e => e.key === 'Enter' && onSaveView()}
                data-testid="save-view-name-input"
              />
              <Button size="sm" onClick={onSaveView} className="bg-[#2E5C8A]" data-testid="save-view-confirm-btn">
                Save
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Saves: {GRANULARITIES.find(g => g.id === granularity)?.label}, {DATE_PRESETS.find(p => p.id === datePreset)?.label}, {selectedMetrics.length} metrics
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSavedViews && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg w-80 z-40 max-h-80 overflow-y-auto"
            data-testid="saved-views-dropdown"
          >
            {savedViews.length > 0 ? (
              <div className="p-2">
                {savedViews.map(view => (
                  <div key={view.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg group">
                    <button
                      onClick={() => onLoadView(view)}
                      className="flex-1 text-left"
                      data-testid={`load-view-${view.id}`}
                    >
                      <p className="text-sm font-medium text-slate-700">{view.name}</p>
                      <p className="text-xs text-slate-400">
                        {GRANULARITIES.find(g => g.id === view.config.granularity)?.label} &middot; {DATE_PRESETS.find(p => p.id === view.config.datePreset)?.label}
                      </p>
                    </button>
                    <button
                      onClick={() => onDeleteView(view.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-view-${view.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center">
                <FolderOpen className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No saved views yet</p>
                <p className="text-xs text-slate-400 mt-1">Save your favorite configurations</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
